import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { useClientsContext } from "@/components/clients-context"
import type { Client } from "@/types/client"
import { InfoTooltip } from "@/components/ui/info-tooltip"
import { useTranslation } from "react-i18next"

interface CreateClientDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onClientCreated?: (client: Client) => void
}

const CURRENCIES = [
    { value: 'EUR', label: '€ EUR - Euro' },
    { value: 'USD', label: '$ USD - Dólar' },
    { value: 'GBP', label: '£ GBP - Libra' },
    { value: 'MXN', label: '$ MXN - Peso Mexicano' },
    { value: 'ARS', label: '$ ARS - Peso Argentino' },
]

export function CreateClientDialog({ open, onOpenChange, onClientCreated }: CreateClientDialogProps) {
    const { t } = useTranslation()
    const { createClient } = useClientsContext()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [name, setName] = useState("")
    const [email, setEmail] = useState("")
    const [fiscalAddress, setFiscalAddress] = useState("")
    const [currency, setCurrency] = useState("EUR")
    const [notes, setNotes] = useState("")

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)

        try {
            const newClient = await createClient({
                name,
                email: email || null,
                fiscal_address: fiscalAddress || null,
                default_currency: currency,
                notes: notes || null
            })

            if (newClient) {
                onClientCreated?.(newClient)
                // Reset form
                setName("")
                setEmail("")
                setFiscalAddress("")
                setCurrency("EUR")
                setNotes("")
                onOpenChange(false)
            }
        } catch (error) {
            console.error("Error creating client:", error)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>{t('clients.dialogs.create.title')}</DialogTitle>
                    <DialogDescription>
                        {t('clients.dialogs.create.description')}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">{t('clients.dialogs.create.nameLabel')}</Label>
                        <Input
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('clients.dialogs.create.namePlaceholder')}
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="email" className="flex items-center">
                            {t('clients.dialogs.create.emailLabel')}
                            <InfoTooltip content={t('clients.dialogs.create.emailTooltip')} />
                        </Label>
                        <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('clients.dialogs.create.emailPlaceholder')}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="fiscalAddress">{t('clients.dialogs.create.addressLabel')}</Label>
                        <Input
                            id="fiscalAddress"
                            value={fiscalAddress}
                            onChange={(e) => setFiscalAddress(e.target.value)}
                            placeholder={t('clients.dialogs.create.addressPlaceholder')}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="currency">{t('clients.dialogs.create.currencyLabel')}</Label>
                        <Select value={currency} onValueChange={setCurrency}>
                            <SelectTrigger id="currency">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CURRENCIES.map((curr) => (
                                    <SelectItem key={curr.value} value={curr.value}>
                                        {curr.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="notes" className="flex items-center">
                            {t('clients.dialogs.create.notesLabel')}
                            <InfoTooltip content={t('clients.dialogs.create.notesTooltip')} />
                        </Label>
                        <Textarea
                            id="notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder={t('clients.dialogs.create.notesPlaceholder')}
                            rows={3}
                        />
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isSubmitting}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? t('clients.dialogs.create.submitting') : t('clients.dialogs.create.submit')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
